import { useState, useEffect } from "react";
import { ArrowRight, Sparkles, ChevronRight, Building2, Users, BarChart3, CreditCard } from "lucide-react";
import { useFlexOfficeCTA } from "./useFlexOfficeCTA";
import data from "../data/flexOfficeData.json";

// Map icon strings from JSON to actual components
const ICON_MAP = { Building2, Users, BarChart3, CreditCard };

const BLUE = "#2563eb";
const BLUE_LIGHT = "#60a5fa";
const SKY = "#38bdf8";
const NAVY = "#080e1a";

const useBreakpoint = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return { isMobile: w < 640, isTablet: w >= 640 && w < 1024 };
};

const FlexOfficeHero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { isMobile, isTablet } = useBreakpoint();
  const cta = useFlexOfficeCTA();
  const d = data.hero;

  useEffect(() => {
    const imageCount = d.carouselImages.length;
    const iv = setInterval(() => setCurrentSlide(p => (p + 1) % imageCount), 5500);
    return () => clearInterval(iv);
  }, [d.carouselImages.length]);

  return (
    <section style={{
      position: "relative",
      minHeight: isMobile ? "auto" : "100vh",
      display: "flex",
      alignItems: "center",
      background: NAVY,
      overflow: "hidden",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Carousel */}
      {d.carouselImages.map((img, i) => (
        <div key={i} style={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center",
          opacity: i === currentSlide ? 1 : 0, transition: "opacity 1.4s ease",
        }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(110deg, rgba(8,14,26,0.90) 0%, rgba(8,14,26,0.70) 48%, rgba(8,14,26,0.88) 100%)" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "40%", background: "linear-gradient(to top, rgba(8,14,26,0.98), transparent)" }} />
        </div>
      ))}

      {/* Glows */}
      <div style={{ position: "absolute", top: "-8%", left: "-6%", width: isMobile ? 260 : 560, height: isMobile ? 260 : 560, borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.16) 0%, transparent 65%)", zIndex: 1, pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-6%", right: "-4%", width: isMobile ? 220 : 480, height: isMobile ? 220 : 480, borderRadius: "50%", background: "radial-gradient(circle, rgba(56,189,248,0.10) 0%, transparent 65%)", zIndex: 1, pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)", backgroundSize: "60px 60px", zIndex: 1, pointerEvents: "none" }} />

      {/* Content */}
      <div style={{
        position: "relative", zIndex: 2, maxWidth: 1280, margin: "0 auto",
        padding: isMobile ? "80px 20px 60px" : isTablet ? "90px 32px 70px" : "110px 48px 90px",
        width: "100%",
      }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile || isTablet ? "1fr" : "1.1fr 0.9fr",
          gap: isMobile ? 36 : isTablet ? 48 : 72,
          alignItems: "center",
        }}>
          {/* LEFT */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(37,99,235,0.14)", border: "1px solid rgba(96,165,250,0.28)", padding: "8px 20px", borderRadius: 100, marginBottom: 28 }}>
              <Sparkles style={{ width: 13, height: 13, color: BLUE_LIGHT }} />
              <span style={{ color: BLUE_LIGHT, fontSize: isMobile ? 10 : 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>{d.badge}</span>
            </div>

            <h1 style={{ fontSize: isMobile ? 48 : isTablet ? 58 : 72, fontWeight: 900, lineHeight: 1.0, letterSpacing: "-0.03em", marginBottom: 22, color: "white" }}>
              <span style={{ display: "block", fontWeight: 400, fontSize: isMobile ? 18 : isTablet ? 26 : 36, color: "rgba(255,255,255,0.50)", letterSpacing: "-0.01em", marginBottom: 6 }}>{d.headline.preText}</span>
              <span style={{ display: "block" }}>{d.headline.main}</span>
              <span style={{ display: "block", background: `linear-gradient(90deg, ${BLUE_LIGHT} 0%, ${SKY} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>{d.headline.accent}</span>
            </h1>

            <p style={{ fontSize: isMobile ? 15 : 17, color: "rgba(255,255,255,0.50)", lineHeight: 1.78, maxWidth: 460, marginBottom: 36 }}>{d.description}</p>

            {/* ── CTA Buttons ── */}
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, flexWrap: "wrap", marginBottom: 36 }}>
              {/* Book a Demo */}
              <button
                onClick={cta.bookDemo}
                style={{ background: "rgba(37,99,235,0.22)", color: "rgba(255,255,255,0.92)", border: "1px solid rgba(96,165,250,0.32)", padding: "15px 36px", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 9, backdropFilter: "blur(8px)", transition: "all 0.25s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(37,99,235,0.34)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.55)"; e.currentTarget.style.color = "white"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(37,99,235,0.22)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.32)"; e.currentTarget.style.color = "rgba(255,255,255,0.92)"; e.currentTarget.style.transform = "none"; }}>
                {d.buttons[0].label} <ArrowRight style={{ width: 16, height: 16 }} />
              </button>

              {/* View Pricing — scrolls to #flexoffice-pricing */}
              <button
                onClick={cta.viewPricing}
                style={{ background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.80)", border: "1px solid rgba(255,255,255,0.16)", padding: "15px 36px", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", backdropFilter: "blur(8px)", transition: "all 0.25s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "white"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "rgba(255,255,255,0.80)"; }}>
                {d.buttons[1].label}
              </button>
            </div>

            <div style={{ display: "flex", gap: isMobile ? 14 : 28, flexWrap: "wrap" }}>
              {d.trustPoints.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: t.color, flexShrink: 0 }} />
                  <span style={{ color: "rgba(255,255,255,0.36)", fontSize: 13 }}>{t.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — updated feature cards (hidden on mobile) */}
          {!isMobile && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {d.featureCards.map((card, i) => {
                const IconComp = ICON_MAP[card.icon];
                return (
                  <div
                    key={i}
                    onClick={cta.getStarted}
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 14, padding: isTablet ? "14px 16px" : "17px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", backdropFilter: "blur(10px)", transition: "all 0.25s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(96,165,250,0.45)"; e.currentTarget.style.background = "rgba(37,99,235,0.10)"; e.currentTarget.style.transform = "translateX(4px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.09)"; e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.transform = "none"; }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ width: 44, height: 44, background: `linear-gradient(135deg, ${BLUE}, #3b82f6)`, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", color: "white", flexShrink: 0, boxShadow: "0 4px 14px rgba(37,99,235,0.36)" }}>
                        {IconComp && <IconComp style={{ width: 20, height: 20 }} />}
                      </div>
                      <div>
                        <div style={{ color: "rgba(255,255,255,0.92)", fontWeight: 700, fontSize: isTablet ? 13 : 14 }}>{card.label}</div>
                        <div style={{ color: "rgba(255,255,255,0.40)", fontSize: isTablet ? 11 : 12.5, marginTop: 2 }}>{card.description}</div>
                      </div>
                    </div>
                    <ChevronRight style={{ width: 15, height: 15, color: "rgba(255,255,255,0.26)" }} />
                  </div>
                );
              })}

              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "13px 18px", textAlign: "center" }}>
                <p style={{ color: "rgba(255,255,255,0.26)", fontSize: 12, marginBottom: 9 }}>And much more included in every plan</p>
                <div style={{ display: "flex", gap: 7, justifyContent: "center", flexWrap: "wrap" }}>
                  {d.extraTags.map(tag => (
                    <span key={tag} style={{ background: "rgba(37,99,235,0.18)", color: BLUE_LIGHT, fontSize: 11, padding: "4px 11px", borderRadius: 100, fontWeight: 600 }}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Mobile: feature tag pills */}
          {isMobile && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {d.featureCards.map(card => (
                <span key={card.label} style={{ background: "rgba(37,99,235,0.2)", color: BLUE_LIGHT, fontSize: 12, padding: "6px 14px", borderRadius: 100, fontWeight: 600, border: "1px solid rgba(96,165,250,0.25)" }}>{card.label}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Slide indicators */}
      <div style={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 8, zIndex: 10 }}>
        {d.carouselImages.map((_, i) => (
          <button key={i} onClick={() => setCurrentSlide(i)}
            style={{ width: i === currentSlide ? 28 : 8, height: 8, borderRadius: 4, border: "none", cursor: "pointer", transition: "all 0.3s", background: i === currentSlide ? BLUE_LIGHT : "rgba(255,255,255,0.24)" }} />
        ))}
      </div>
    </section>
  );
};

export default FlexOfficeHero;
